"use client";

import { useEffect, useRef } from "react";
import { usePathname } from "next/navigation";

const LOADER_ID = "hubspot-web-interactives-loader";

/**
 * Rebinds HubSpot CTA buttons after a client-side navigation.
 *
 * HubSpot's web-interactives bundle scans the DOM for trigger elements once, on
 * boot, and binds click handlers to whatever it finds. Buttons that arrive later
 * are never scanned, so clicking them does nothing. Re-running the script is the
 * only reliable way to repeat that scan; the bundle refuses to boot twice unless
 * `hubspot_web_interactives_running` is cleared first.
 *
 * The src is copied from the loader tag instead of hardcoded because the bundle
 * resolves its portal and hublet from `#hubspot-web-interactives-loader`, and
 * falls back to the `na1` hublet when that tag is missing.
 */
export const useReinitHubspotCta = () => {
  const pathname = usePathname();
  const isFirstRender = useRef(true);

  useEffect(() => {
    // On a cold load HubSpot boots itself and its scan already covers the page.
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return undefined;
    }

    const loader = document.getElementById(LOADER_ID) as HTMLScriptElement | null;
    if (!loader) return undefined;

    window.hubspot_web_interactives_running = false;

    const script = document.createElement("script");
    script.src = loader.src;
    if (loader.crossOrigin) script.crossOrigin = loader.crossOrigin;
    document.head.appendChild(script);

    return () => {
      script.remove();
    };
  }, [pathname]);
};
